import { FilterListOffRounded, IosShareRounded } from "@mui/icons-material";
import { Box, Button, Card, Grid, Stack, styled, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useState } from "react";
import { useParams } from "react-router";
import { SurveyInstancePicker, Table, TableSkeleton, TeamPicker } from "../../../components";
import { GetSurveyResponsesReportResultDto, useGetSurveyAnswersReport } from "../../../http";
import { downloadCsv, setTitlePrefix } from "../../../utilities";

const ToolBar = styled(Stack)`
  display: flex;
  flex-direction: row;
  position: relative;
  margin-top: -20px;
  margin-bottom: 20px;
  justify-content: space-between;
  left: -20px;
  width: calc(100% + 40px);
  min-height: 50px;
  padding: 6px 20px;
  background: rgba(248, 249, 250, 0.7);
  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.1);
`;

interface IParams {
  guid: string;
}

const columnHelper = createColumnHelper<Record<string, string>>();

function handleGenerateColumns({ data }: GetSurveyResponsesReportResultDto) {
  const columns = [
    columnHelper.accessor("responseGuid", { id: "Response ID", header: "Response ID", size: 120, minSize: 120 }),
    columnHelper.accessor("instanceGuid", { id: "Instance ID", header: "Instance ID", size: 300, minSize: 200 }),
    columnHelper.accessor("submittedDateTimeUtc", {
      id: "Submission Time",
      header: "Submission Time",
      size: 220,
      minSize: 180,
    }),
    columnHelper.accessor("teamGuid", { id: "Team ID", header: "Team ID" }),
    columnHelper.accessor("teamName", { id: "Team Name", header: "Team Name", size: 220, minSize: 150 }),
    columnHelper.accessor("joinedDate", { id: "Date Joined", header: "Date Joined", size: 220, minSize: 150 }),
    columnHelper.accessor("location", { id: "Location", header: "Location", size: 220, minSize: 150 }),
  ];

  const questionColumns = data.columns.map((column, index) =>
    columnHelper.accessor(`question${index + 1}`, {
      id: `Question ${index + 1}`,
      header: column.header,
      size: 220,
      minSize: 150,
    }),
  );

  return [...columns, ...questionColumns];
}

function handleGenerateRows({ data }: GetSurveyResponsesReportResultDto, selectedTeamGuids: string[]) {
  return data.rows
    .filter((row) => selectedTeamGuids.length === 0 || selectedTeamGuids.includes(row.teamGuid))
    .map((row) => {
      const newRow: Record<string, string> = {
        responseGuid: row.responseGuid,
        instanceGuid: row.instanceGuid,
        submittedDateTimeUtc:
          row.submittedDateTimeUtc != null
            ? DateTime.fromISO(row.submittedDateTimeUtc, { zone: "utc" })
                .toLocal()
                .toLocaleString(DateTime.DATETIME_FULL)
            : "",
        teamGuid: row.teamGuid,
        teamName: row.teamName,
        joinedDate:
          row.joinedDate != null
            ? DateTime.fromISO(row.joinedDate, { zone: "utc" }).toLocal().toLocaleString(DateTime.DATE_MED)
            : "",
        location: `${row.countryCode}${row.cityName ? `, ${row.cityName}` : ""}`,
      };

      data.columns.forEach((column, index) => {
        const questionKey = `question${index + 1}`;
        const answer = row.answers.find((answer) => answer.questionGuid === column.guid);
        newRow[questionKey] = answer?.value || "";
      });

      return newRow;
    });
}

const SurveyResponsesPage = () => {
  setTitlePrefix("Survey Responses");

  const { guid: surveyGuid } = useParams<keyof IParams>() as IParams;
  const [surveyInstanceGuid, setSurveyInstanceGuid] = useState<string | undefined>(undefined);
  const [selectedTeamGuids, setSelectedTeamGuids] = useState<string[]>([]);
  const { data: answersReport, isLoading: isLoadingAnswersReport } = useGetSurveyAnswersReport(
    surveyGuid,
    surveyInstanceGuid,
  );

  const columns = answersReport ? handleGenerateColumns(answersReport) : [];
  const rows = answersReport ? handleGenerateRows(answersReport, selectedTeamGuids) : [];

  function handleClearFiltersClicked() {
    setSurveyInstanceGuid(undefined);
    setSelectedTeamGuids([]);
  }

  function handleExportCsvClicked() {
    const csvData: string[] = [];

    // Push headers
    csvData.push(columns.map((x) => `"${x.id!}"`).join(","));

    // Push rows
    rows.forEach((row) => {
      csvData.push(
        Object.values(row)
          .map((x) => `"${x}"`)
          .join(","),
      );
    });

    downloadCsv({
      filename: `survey-responses-${DateTime.now().toISODate()}`,
      csv: csvData.join("\n"),
    });
  }

  return (
    <Grid>
      <ToolBar>
        <Stack direction="row" spacing={1}>
          <SurveyInstancePicker
            surveyGuid={surveyGuid}
            surveyInstanceGuid={surveyInstanceGuid}
            onChange={(surveyInstance) => setSurveyInstanceGuid(surveyInstance.guid)}
          />
          <TeamPicker
            selectedTeamGuids={selectedTeamGuids}
            onTeamSelected={(selectedTeamGuids) => setSelectedTeamGuids(selectedTeamGuids)}
            onClearClicked={() => setSelectedTeamGuids([])}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="tertiary" startIcon={<FilterListOffRounded />} onClick={handleClearFiltersClicked}>
            Clear Filters
          </Button>
          <Button variant="tertiary" startIcon={<IosShareRounded />} onClick={handleExportCsvClicked}>
            Export CSV
          </Button>
        </Stack>
      </ToolBar>

      <Stack spacing={1}>
        <Card>
          {isLoadingAnswersReport ? (
            <TableSkeleton />
          ) : (
            <Table
              tableId="survey-responses-table"
              data={rows}
              columns={columns}
              placeholder={
                <Box sx={{ m: 1 }}>
                  <Typography variant="placeholder">Nothing here yet.</Typography>
                </Box>
              }
            />
          )}
        </Card>
      </Stack>
    </Grid>
  );
};

export { SurveyResponsesPage };
